import { Mail, MailOpen, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { API, errMsg, formatDate } from "@/lib/api";

export default function AdminEnquiries() {
  const [items, setItems] = useState(null);

  const load = () => API.get("/admin/enquiries").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const toggleRead = async (e2) => {
    try {
      await API.patch(`/admin/enquiries/${e2.id}`, { read: !e2.read });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (e2) => {
    try {
      await API.delete(`/admin/enquiries/${e2.id}`);
      toast.success("Enquiry deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="admin-enquiries-page">
      <h1 className="font-display text-3xl tracking-tight mb-6">Enquiries</h1>
      {!items ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-center py-16 border rounded-xl" data-testid="enquiries-empty">No enquiries yet. Messages sent through the contact form will appear here.</p>
      ) : (
        <div className="space-y-3" data-testid="enquiries-list">
          {items.map((e) => (
            <Card key={e.id} className={!e.read ? "border-primary/40" : ""} data-testid={`enquiry-${e.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{e.name}</p>
                      {!e.read && <Badge className="rounded-full" data-testid={`unread-badge-${e.id}`}>New</Badge>}
                      <span className="text-xs text-muted-foreground">{formatDate(e.created_at)}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      <a href={`mailto:${e.email}`} className="text-primary hover:underline">{e.email}</a>
                      {e.phone && <> · <a href={`tel:${e.phone}`} className="text-primary hover:underline">{e.phone}</a></>}
                    </p>
                    <p className="text-sm mt-2 whitespace-pre-line">{e.message}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => toggleRead(e)} data-testid={`toggle-read-${e.id}`} aria-label={e.read ? "Mark unread" : "Mark read"}>
                      {e.read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove(e)} data-testid={`delete-enquiry-${e.id}`} aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
